import { useCallback, useEffect, useState } from "react";
import { authClient } from "@/auth/client";
import type {
  AdminRole,
  AdminUser,
  CreateAdminUserInput,
  UpdateAdminUserInput,
} from "@/types/admin";

const USER_LIMIT = 100;
const allowCredentialManagement = process.env.NODE_ENV !== "production";

function userListQuery(search: string) {
  return {
    limit: USER_LIMIT,
    offset: 0,
    sortBy: "createdAt",
    sortDirection: "desc" as const,
    ...(search
      ? {
          searchValue: search,
          searchField: "email" as const,
          searchOperator: "contains" as const,
        }
      : {}),
  };
}

interface AuthClientError {
  message?: string | null;
  code?: string | null;
}

function getAuthErrorMessage(error: AuthClientError | null | undefined, fallback: string) {
  if (!error) return fallback;
  return error.message || error.code || fallback;
}

export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = useCallback(
    async (nextSearch = search) => {
      setLoading(true);
      setError(null);

      const { data, error } = await authClient.admin.listUsers({
        query: userListQuery(nextSearch),
      });

      if (error) {
        setUsers([]);
        setTotal(0);
        setError(getAuthErrorMessage(error, "Unable to load users."));
      } else {
        setUsers(data?.users ?? []);
        setTotal(data?.total ?? 0);
      }

      setLoading(false);
    },
    [search]
  );

  useEffect(() => {
    let active = true;

    authClient.admin
      .listUsers({ query: userListQuery("") })
      .then(({ data, error }) => {
        if (!active) return;

        if (error) {
          setUsers([]);
          setTotal(0);
          setError(getAuthErrorMessage(error, "Unable to load users."));
        } else {
          setUsers(data?.users ?? []);
          setTotal(data?.total ?? 0);
        }
      })
      .catch((caught) => {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : "Unable to load users.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const runMutation = useCallback(
    async (mutation: () => Promise<void>) => {
      setSaving(true);
      setError(null);

      try {
        await mutation();
        await loadUsers();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Unable to save user changes.");
        throw caught;
      } finally {
        setSaving(false);
      }
    },
    [loadUsers]
  );

  const createUser = useCallback(
    async (input: CreateAdminUserInput) => {
      if (!allowCredentialManagement) {
        throw new Error("User creation is disabled in production.");
      }

      await runMutation(async () => {
        const { error } = await authClient.admin.createUser({
          email: input.email,
          name: input.name,
          password: input.password,
          role: input.role,
        });

        if (error) throw new Error(getAuthErrorMessage(error, "Unable to create user."));
      });
    },
    [runMutation]
  );

  const updateUser = useCallback(
    async (input: UpdateAdminUserInput) => {
      await runMutation(async () => {
        const updateResult = await authClient.admin.updateUser({
          userId: input.userId,
          data: {
            name: input.name,
            email: input.email,
          },
        });

        if (updateResult.error) {
          throw new Error(getAuthErrorMessage(updateResult.error, "Unable to update user."));
        }

        const roleResult = await authClient.admin.setRole({
          userId: input.userId,
          role: input.role,
        });

        if (roleResult.error) {
          throw new Error(getAuthErrorMessage(roleResult.error, "Unable to update role."));
        }

        if (allowCredentialManagement && input.password) {
          const passwordResult = await authClient.admin.setUserPassword({
            userId: input.userId,
            newPassword: input.password,
          });

          if (passwordResult.error) {
            throw new Error(
              getAuthErrorMessage(passwordResult.error, "Unable to update password.")
            );
          }
        }
      });
    },
    [runMutation]
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      await runMutation(async () => {
        const { error } = await authClient.admin.removeUser({ userId });
        if (error) throw new Error(getAuthErrorMessage(error, "Unable to delete user."));
      });
    },
    [runMutation]
  );

  const setUserRole = useCallback(
    async (userId: string, role: AdminRole) => {
      await runMutation(async () => {
        const { error } = await authClient.admin.setRole({ userId, role });
        if (error) throw new Error(getAuthErrorMessage(error, "Unable to update role."));
      });
    },
    [runMutation]
  );

  return {
    users,
    total,
    search,
    setSearch,
    loading,
    saving,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    setUserRole,
  };
}
